import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ApiService, ResponseCode } from '@lsq-frontend/angular-shared/api-service';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { FastTrackApiRoutes, FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { VoidResponse } from '@lsq-frontend/remote-services/enterprise/common';
import {
    AuthenticationService,
    forLifeOf,
    LifecycleAware,
    Masks,
    SEND_PHONE_NUMBER_VERIFICATION_CODE_SERVICE,
    Unmasks,
    VALIDATE_PHONE_NUMBER_VERIFICATION_CODE_SERVICE,
    FeatureFlagService,
    focusOnFirstInvalidLsqInput,
    LsqValidators
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { LoginPageStep, LoginService } from '../login.service';

@LifecycleAware()
@Component({
    selector: 'sms-mfa-registration',
    styleUrls: ['./login-step.scss'],
    template: `
        <ng-container *ngIf="!showVerification">
            <h1>Multi-Factor Authentication</h1>
            <div class="enrollSMS">
                <form class="login-step-wide" [formGroup]="phoneFormGroup" (ngSubmit)="sendCode()">
                    <p class="body" [style.marginBottom]="'2rem'">
                        To confirm your identity and verify your account, we'll send a one-time
                        verification via SMS. By providing your mobile number, you agree to receive
                        text messages.
                    </p>

                    <lsq-phone-input
                        *ngIf="countryCodePhoneInputIsOn$ | async"
                        [style.marginBottom]="'3rem'"
                        [style.marginTop]="'1.5rem'"
                        [group]="phoneFormGroup"
                        formControlName="phoneNumber"
                        label="Mobile Phone Number"
                        name="phoneNumber"
                    ></lsq-phone-input>

                    <ng-container *ngIf="!(countryCodePhoneInputIsOn$ | async)">
                        <p class="label" [style.marginBottom]="'-0.5rem'">
                            Mobile Phone Number
                        </p>
                        <lsq-input
                            [style.marginBottom]="'3rem'"
                            name="phoneNumber"
                            [basicLabel]="false"
                            formControlName="phoneNumber"
                            [group]="phoneFormGroup"
                            type="tel"
                            [mask]="masks.PhoneMask"
                            [unmask]="phoneUnmask"
                            [autoFocus]="true"
                        >
                        </lsq-input>
                    </ng-container>

                    <div class="button-container">
                        <lsq-button
                            appearance="secondary"
                            size="block"
                            [buttonClass]="'back-btn'"
                            (buttonClick)="goBack()"
                            class="login-button-small"
                            [style.marginRight]="'2rem'"
                        >
                            Back
                        </lsq-button>
                        <lsq-button
                            type="submit"
                            size="block"
                            [submitting]="isSubmitting"
                            class="login-button-small"
                        >
                            Send Code
                        </lsq-button>
                    </div>
                </form>
            </div>
        </ng-container>

        <ng-container *ngIf="showVerification">
            <h1>Verification</h1>

            <form class="login-step-wide" [formGroup]="verifyFormGroup" (ngSubmit)="submitCode()">
                <p class="body" [style.marginBottom]="'2rem'">
                    Enter the one-time verification code that was sent via SMS to the mobile number
                    ending in {{ phoneNumberLastFour }}.
                    <lsq-button
                        appearance="transparent"
                        (buttonClick)="editPhoneNumber()"
                        [style.marginLeft]="'1rem'"
                        [style.display]="'inline-block'"
                    >
                        Edit
                    </lsq-button>
                </p>

                <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
                <p class="label" [style.marginBottom]="'-0.5rem'">Enter Verification Code</p>
                <lsq-input
                    [style.marginBottom]="'1rem'"
                    name="mfaCode"
                    [basicLabel]="false"
                    formControlName="mfaCode"
                    [group]="verifyFormGroup"
                    [maxLength]="6"
                    [autoFocus]="true"
                >
                </lsq-input>

                <p [style.marginBottom]="'2rem'">
                    Didn't receive a code?

                    <lsq-button
                        appearance="transparent"
                        (buttonClick)="sendCode()"
                        [style.marginLeft]="'1rem'"
                        [style.display]="'inline-block'"
                    >
                        Resend
                    </lsq-button>
                </p>

                <div class="button-container">
                    <lsq-button
                        appearance="secondary"
                        size="block"
                        [buttonClass]="'back-btn'"
                        (buttonClick)="goBack()"
                        class="login-button-small"
                        [style.marginRight]="'2rem'"
                    >
                        Back
                    </lsq-button>
                    <lsq-button
                        type="submit"
                        size="block"
                        [submitting]="isSubmitting"
                        class="login-button-small"
                    >
                        Continue
                    </lsq-button>
                </div>
            </form>
        </ng-container>
    `
})
export class SMSMfaRegistrationComponent {
    public readonly masks = Masks;
    public readonly phoneUnmask = Unmasks.PhoneUnmask;
    public phoneNumberLastFour: string;
    public showVerification = false;
    public isSubmitting = false;
    private _mfaCodeFailure = false;

    public phoneFormGroup = this._formBuilder.group({
        phoneNumber: ['', [Validators.required, LsqValidators.phone]]
    });

    public verifyFormGroup = this._formBuilder.group({
        mfaCode: [
            '',
            [
                Validators.required,
                LsqValidators.number,
                LsqValidators.exactLength(6),
                () =>
                    this._mfaCodeFailure
                        ? { mfaCodeInvalid: { message: this._invalidAuthCodeCopy } }
                        : null
            ]
        ]
    });

    private readonly _invalidAuthCodeCopy = this._i18n({
        value: 'Invalid Code'
    });

    public countryCodePhoneInputIsOn$ = this._featureFlagService.isOn(
        FeatureFlags.CountryCodePhoneInput
    );

    constructor(
        private _i18n: I18n,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _formBuilder: FormBuilder,
        private _loginService: LoginService,
        @Inject(VALIDATE_PHONE_NUMBER_VERIFICATION_CODE_SERVICE)
        private _validatePhoneNumberVerificationCodeService: ApiService<any, VoidResponse>,
        @Inject(SEND_PHONE_NUMBER_VERIFICATION_CODE_SERVICE)
        private _sendPhoneNumberVerificationCodeService: ApiService<any, VoidResponse>,
        private _featureFlagService: FeatureFlagService,
        private _authProviderService: AuthenticationService
    ) {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidatePhoneNumberVerificationCode}`,
            ResponseCode.InvalidRequest
        );

        this.verifyFormGroup.valueChanges.pipe(forLifeOf(this)).subscribe(() => {
            this._mfaCodeFailure = false;
        });
    }

    public async sendCode(): Promise<void> {
        this.phoneFormGroup.markAllAsTouched();

        if (this.phoneFormGroup.valid) {
            this.isSubmitting = true;

            const phoneNumber = this.phoneFormGroup.get('phoneNumber').value;
            this.phoneNumberLastFour = phoneNumber.substr(phoneNumber.length - 4);

            await this._sendPhoneNumberVerificationCodeService.executeOnce({ phoneNumber });

            this.isSubmitting = false;
            this.showVerification = true;
        }
    }

    public async submitCode(): Promise<void> {
        this._mfaCodeFailure = false;
        this.verifyFormGroup.markAllAsTouched();
        this.verifyFormGroup.get('mfaCode').updateValueAndValidity();

        if (this.verifyFormGroup.valid) {
            this.isSubmitting = true;

            const mfaCode = this.verifyFormGroup.get('mfaCode').value;

            try {
                await this._validatePhoneNumberVerificationCodeService.executeOnce({
                    verificationCode: mfaCode
                });

                this._loginService.userIsRegisteredForMfa = true;
                await this._authProviderService.setUserPreferredMfa('SMS_MFA');
                await this._loginService.finishLogin();
            } catch (error) {
                this._mfaCodeFailure = true;
                this.verifyFormGroup.get('mfaCode').updateValueAndValidity();
                focusOnFirstInvalidLsqInput();
            }

            this.isSubmitting = false;
        }
    }

    public editPhoneNumber(): void {
        this.showVerification = false;
    }

    public goBack(): void {
        if (this.showVerification) {
            this.showVerification = false;
        } else {
            this._loginService.setLoginPageStep(LoginPageStep.MfaRegistration);
        }
    }
}
